import React, { useState } from 'react';
import { Box, Button, Typography, CircularProgress } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import DescriptionIcon from '@material-ui/icons/Description';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';

const DocumentViewer = ({ url }) => {
  // State Management
  const [showPreview, setShowPreview] = useState(false);
  const [textContent, setTextContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pdfError, setPdfError] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);

  // Effects
  React.useEffect(() => {
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [pdfBlobUrl]);

  if (!url) return null;

  // Utility Functions
  const isAzureBlobStorage = (url) => {
    return url.includes('.blob.core.windows.net');
  };

  const getDocumentInfo = (url) => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const filename = pathname.split('/').pop() || 'Document';
      
      // Determine document type and icon
      const extension = filename.split('.').pop().toLowerCase();
      let documentType = 'Document';
      let Icon = InsertDriveFileIcon;
      let canPreview = false;
      
      switch (extension) {
        case 'pdf':
          documentType = 'PDF Document';
          Icon = DescriptionIcon;
          canPreview = true;
          break;
        case 'doc':
        case 'docx':
          documentType = 'Word Document';
          break;
        case 'xls':
        case 'xlsx':
          documentType = 'Excel Spreadsheet';
          break;
        case 'csv':
          documentType = 'CSV File';
          canPreview = true;
          break;
        case 'txt':
          documentType = 'Text File';
          canPreview = true;
          break;
        case 'rtf':
          documentType = 'Rich Text Document';
          break;
        default:
          documentType = 'Generic Document';
          break;
      }
      
      // Check for cloud storage services
      if (url.includes('drive.google.com')) {
        documentType = 'Google Drive Document';
        canPreview = false;
      } else if (url.includes('dropbox.com')) {
        documentType = 'Dropbox Document';
        canPreview = false;
      } else if (url.includes('1drv.ms')) {
        documentType = 'OneDrive Document';
        canPreview = false;
      } else if (isAzureBlobStorage(url)) {
        documentType = 'Azure Blob Storage Document';
        canPreview = extension === 'pdf';
      }
      
      return {
        filename,
        documentType,
        Icon,
        extension,
        canPreview
      };
    } catch (e) {
      return {
        filename: 'Document',
        documentType: 'Document',
        Icon: InsertDriveFileIcon,
        extension: '',
        canPreview: false
      };
    }
  };
  
  // Data Loading Functions
  const loadTextContent = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to load document');
      }
      const text = await response.text();
      setTextContent(text);
    } catch (err) {
      if (err.message === 'Failed to fetch') {
        setError('Preview unavailable due to browser security restrictions. Please use the View or Download buttons to access the document.');
      } else {
        setError('Unable to load document preview');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadPdfAsBlob = async () => {
    setLoading(true);
    setPdfError(false);
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to load PDF');
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPdfBlobUrl(blobUrl);
    } catch (err) {
      console.error('Error loading PDF as blob:', err);
      setPdfError(true);
    } finally {
      setLoading(false);
    }
  };

  const documentInfo = getDocumentInfo(url);

  return (
    <Box style={{ width: '100%' }}>
      <Box style={{
        padding: '16px',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        backgroundColor: '#fafafa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <Box style={{ 
          display: 'flex', 
          alignItems: 'center',
          minWidth: 0,
          flex: '1 1 auto'
        }}>
          <documentInfo.Icon style={{ 
            fontSize: '48px', 
            color: '#666',
            marginRight: '16px',
            flexShrink: 0
          }} />
          <Box style={{ minWidth: 0 }}>
            <Typography variant="subtitle1" style={{ 
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {documentInfo.filename}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {documentInfo.documentType}
            </Typography>
          </Box>
        </Box>
        <Box style={{ display: 'flex', gap: '8px' }}>
          {documentInfo.canPreview && (
            <Button
              variant="outlined"
              size="small"
              startIcon={showPreview ? <VisibilityOffIcon /> : <VisibilityIcon />}
              onClick={() => {
                if (!showPreview) {
                  setPdfError(false);
                  if (documentInfo.extension === 'pdf' && isAzureBlobStorage(url) && !pdfBlobUrl && !loading) {
                    loadPdfAsBlob();
                  } else if (documentInfo.extension !== 'pdf' && !textContent && !loading) {
                    loadTextContent();
                  }
                }
                setShowPreview(!showPreview);
              }}
            >
              {showPreview ? 'Hide' : 'Preview'}
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<OpenInNewIcon />}
            onClick={() => window.open(url, '_blank')}
          >
            View
          </Button>
          <Button
            variant="contained"
            size="small"
            color="primary"
            startIcon={<GetAppIcon />}
            onClick={() => {
              const link = document.createElement('a');
              link.href = url;
              link.download = documentInfo.filename;
              link.target = '_blank';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            Download
          </Button>
        </Box>
      </Box>
      
      {showPreview && documentInfo.canPreview && (
        <Box style={{
          marginTop: '16px',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: '#fff'
        }}>
          {documentInfo.extension === 'pdf' ? (
            isAzureBlobStorage(url) ? (
              loading ? (
                <Box style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
                  <CircularProgress />
                </Box>
              ) : pdfBlobUrl ? (
                <iframe
                  src={pdfBlobUrl}
                  title="PDF Preview"
                  style={{
                    width: '100%',
                    height: '600px',
                    border: 'none'
                  }}
                />
              ) : pdfError ? (
                <Box style={{
                  padding: '40px',
                  textAlign: 'center',
                  backgroundColor: '#f5f5f5'
                }}>
                  <Typography variant="body1" color="textSecondary" gutterBottom>
                    Unable to preview this PDF from Azure Blob Storage.
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Please use the "View" or "Download" buttons to access the document.
                  </Typography>
                </Box>
              ) : null
            ) : pdfError ? (
              <Box style={{
                padding: '40px',
                textAlign: 'center',
                backgroundColor: '#f5f5f5'
              }}>
                <Typography variant="body1" color="textSecondary" gutterBottom>
                  PDF preview is unavailable. This may be due to browser security restrictions.
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginTop: '16px' }}>
                  Please use the "View" button to open the PDF in a new tab, or "Download" to save it locally.
                </Typography>
              </Box>
            ) : (
              <iframe
                src={`${url}#toolbar=0&navpanes=0&scrollbar=0`}
                title="PDF Preview"
                style={{
                  width: '100%',
                  height: '600px',
                  border: 'none'
                }}
                onError={() => setPdfError(true)}
                onLoad={(e) => {
                  // Check if the iframe loaded successfully
                  try {
                    const iframe = e.target;
                    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
                    if (!iframeDoc || iframeDoc.body.innerHTML === '') {
                      setPdfError(true);
                    }
                  } catch (err) {
                    // Cross-origin error is expected for external PDFs
                  }
                }}
              />
            )
          ) : (
            <Box style={{ padding: '16px' }}>
              {loading && (
                <Box style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
                  <CircularProgress />
                </Box>
              )}
              {error && (
                <Box style={{ textAlign: 'center', padding: '32px' }}>
                  <Typography color="error" style={{ marginBottom: '16px' }}>
                    {error}
                  </Typography>
                  {error.includes('browser security restrictions') && (
                    <Box style={{ marginTop: '16px' }}>
                      <Typography variant="body2" color="textSecondary" style={{ marginBottom: '8px' }}>
                        You can still access this document by:
                      </Typography>
                      <Box style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<OpenInNewIcon />}
                          onClick={() => window.open(url, '_blank')}
                        >
                          Open in New Tab
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<GetAppIcon />}
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = documentInfo.filename;
                            link.target = '_blank';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                        >
                          Download
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
              {!loading && !error && textContent && (
                <pre style={{
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: '500px',
                  overflow: 'auto',
                  margin: 0,
                  padding: '16px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px'
                }}>
                  {textContent}
                </pre>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default DocumentViewer;